import {useCallback, useMemo} from 'react';
import {addDoc, collection, deleteDoc, doc, query, updateDoc} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {firestore} from "../App";
import {Holiday, HolidayInsertOptions, HolidayUpdateOptions} from "../types/Holiday";

interface UseHolidayManagerProps {
    plannerId: string;
}

/**
 * Provides functions for managing holidays for a specific planner
 * in Cloud Firestore.
 */
export function useHolidayManager({plannerId}: UseHolidayManagerProps) {

    console.log('useHolidayManager:plannerId', plannerId);

    const holidaysQuery = useMemo(() => {
        if (!plannerId) return null;

        return query(
            collection(firestore, 'syncedPlanners', plannerId, 'holidays')
        );
    }, [plannerId]);

    const [holidaysSnapshot, loadingHolidays, errorHolidays] = useCollection(holidaysQuery);

    console.log('useHolidayManager', holidaysSnapshot?.docs?.length);

    const holidays = useMemo(() => {
        return holidaysSnapshot?.docs.map(doc => ({
            id: doc.id,
            plannerId: plannerId,
            ...doc.data()
        } as Holiday)) || [];
    }, [holidaysSnapshot, plannerId]);

    /**
     * Creates a new holiday document in the planner's subcollection.
     */
    const createHoliday = useCallback(async (options: HolidayInsertOptions) => {
        if (!plannerId) return; // Safety check

        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'holidays');

        await addDoc(colRef, {
            ...options,
            plannerId: plannerId
        });
    }, [plannerId]);

    /**
     * Updates an existing holiday document.
     */
    const updateHoliday = useCallback(async (holidayId: string, options: HolidayUpdateOptions) => {
        if (!plannerId) return; // Safety check

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'holidays', holidayId);
        await updateDoc(docRef, {...options});
    }, [plannerId]);

    /**
     * Deletes a holiday document from the subcollection.
     */
    const deleteHoliday = useCallback(async (holidayId: string) => {
        if (!plannerId) return; // Safety check

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'holidays', holidayId);
        await deleteDoc(docRef);
    }, [plannerId]);

    return {
        holidays,
        loadingHolidays,
        errorHolidays,
        createHoliday,
        updateHoliday,
        deleteHoliday
    };
}